// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {VNode} from 'snabbdom';
import {selx} from 'clsx';

import {UserIconEl} from 'common/widgets/user-icon';
import {Placement} from 'common/components/tooltip/types';
import {TrackingEventProperty as UserIconTrackingEventProperty} from 'common/widgets/user-icon/tracking';
import {TrackedUserIconEl} from 'common/widgets/tracked-user-icon';
import {IconSizeType} from 'common/constants';
import {TooltipEl} from 'common/components/tooltip';
import {getEntityCreatorDisplayName} from 'common/report/stores/user-details';

import {UserIconAttrs} from '../user-icon/types';
import {getLastViewedStr} from './util';
import type {UserList} from './index';
import {EditorUser, isTrackedUserItem, UserItem} from './types';
import {TrackingEventProperty, UserListTrackProperties} from './tracking';
import styles from './index.module.styl';

function getIconEl(user: UserItem, extraAttrs: Partial<UserIconAttrs>) {
  if (isTrackedUserItem(user)) {
    return (
      <TrackedUserIconEl
        attrs={{
          'distinct-id': user[`distinct-id`],
          email: user[`email`],
          avatar: user[`avatar`],
          'tooltip-text': user[`tooltip-text`],
          ...extraAttrs,
        }}
        sel={selx(styles.userIcon)}
      />
    );
  }
  return (
    <UserIconEl
      attrs={{
        'img-src': user[`img-src`],
        id: user[`id`],
        email: user[`email`],
        'label-prefix': user[`label-prefix`] || ``,
        'label-suffix': user[`label-suffix`] || ``,
        'is-offline': user.isOffline,
        ...extraAttrs,
        name: user.name ?? ``, // guard against passing 'null' or 'undefined' strings
      }}
      style={
        user.color
          ? {
              '--border-color': user.color,
              '--border-size': `1px`,
            }
          : undefined
      }
      sel={selx(styles.userIcon)}
    />
  );
}

function OverflowTooltip({
  users,
  extraUsersCount,
  moreText,
  editorUser,
  trackingProps,
  onShowed,
}: {
  users: UserItem[];
  extraUsersCount: number;
  moreText?: string;
  editorUser?: EditorUser;
  trackingProps: UserListTrackProperties;
  onShowed: () => void;
}) {
  if (!users.length) {
    return null;
  }

  const hasLastViewed = users.some((user) => !isTrackedUserItem(user) && !!user.lastViewedUtcSeconds);
  const icons = users.map((user, index) => {
    const commonAttrs: Partial<UserIconAttrs> = {
      'disable-tooltip': true,
      size: IconSizeType.Small,
      'include-label': true,
      'is-offline': false,
      'track-properties': {
        [UserIconTrackingEventProperty.IconType]: index === 0 ? `creator` : `viewer`,
        [UserIconTrackingEventProperty.AccessPoint]: `views-overflow`,
        ...(trackingProps
          ? {
              [UserIconTrackingEventProperty.Source]: trackingProps[TrackingEventProperty.Source],
              [UserIconTrackingEventProperty.ResourceType]: trackingProps[TrackingEventProperty.ResourceType],
            }
          : {}),
      },
    };

    const rowNodes: VNode[] = [getIconEl(user, commonAttrs)];
    if (hasLastViewed && !isTrackedUserItem(user)) {
      let lastViewedStr = user.lastViewedUtcSeconds ? getLastViewedStr(user.lastViewedUtcSeconds) : ``;
      if (user.isOffline === false) {
        lastViewedStr = `now`;
      }
      rowNodes.push(<div sel={selx(styles.secondaryText)}>{lastViewedStr}</div>);
    }

    return <div sel={selx(styles.overflowEntry)}>{rowNodes}</div>;
  });

  const editorUserName = editorUser && getEntityCreatorDisplayName(editorUser.email, editorUser.name);
  return (
    <TooltipEl attrs={{placement: Placement.Bottom}} on={{showed: onShowed}}>
      <div>
        {hasLastViewed && <div sel={selx(styles.overflowHeader, styles.overflowText)}>Recently Viewed By:</div>}
        <div sel={selx(styles.overflowIcons)} class={{'has-columns': hasLastViewed}}>
          {icons}
        </div>

        {extraUsersCount ? (
          <div sel={selx(styles.overflowMoreText, styles.overflowText)}>
            + {extraUsersCount} {moreText}
          </div>
        ) : null}

        {editorUser && (
          <div sel={selx(styles.overflowEditorSection)}>
            <div sel={selx(styles.overflowText)}>
              Last edited {editorUserName ? `by ${editorUserName}\n` : ``}
              {editorUser.dateStr}
            </div>
          </div>
        )}
      </div>
    </TooltipEl>
  );
}

export function Template(this: UserList) {
  const maxIcons = this.attr(`max-icons-displayed`) >= 0 ? this.attr(`max-icons-displayed`) : 0;
  const allUsers = this.attr(`users`) || [];

  const usersToShow = allUsers.slice(0, maxIcons).reverse();
  const overflowUsers = allUsers.slice(maxIcons, this.attr(`max-icons-total`));

  const extraUsersCount = this.attr(`user-count-override`) ?? Math.max(allUsers.length - usersToShow.length, 0);
  const extraOverflowUsersCount = Math.max(extraUsersCount - overflowUsers.length, 0);

  const icons = usersToShow.map((user, index) => {
    return getIconEl(user, {
      'disable-tooltip': this.attr(`disable-tooltip`),
      'tooltip-placement': this.attr(`tooltip-placement`),
      size: this.attr(`size`),
      'track-properties': {
        [UserIconTrackingEventProperty.IconType]: index === 0 ? `creator` : `viewer`,
        [UserIconTrackingEventProperty.AccessPoint]: `creator-icon`,
        [UserIconTrackingEventProperty.ResourceType]: this.attr(`track-properties`)[TrackingEventProperty.ResourceType],
      },
    });
  });

  const moreText = extraUsersCount ? (
    <div sel={selx(styles.moreText)}>
      <span>+ {extraUsersCount}</span>
      <OverflowTooltip
        users={overflowUsers}
        extraUsersCount={extraOverflowUsersCount}
        moreText={this.attr(`more-text`)}
        editorUser={this.attr(`editor-user`)}
        trackingProps={this.attr(`track-properties`)}
        onShowed={this.helpers.handleOverflowTooltipShowed}
      />
    </div>
  ) : null;

  return (
    <div sel={selx(styles.usersList)}>
      <div sel={selx(styles.iconContainer)}>{icons}</div>
      {moreText}
    </div>
  );
}
